import React, {useEffect, useRef, useState} from "react";
import {patch, post} from "../../api/api";
import {
    Button,
    Container,
    FormControl,
    Grid2,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import UploadIcon from "@mui/icons-material/Upload";
import {TemplateItemModel, TemplateModel} from "../../models/TemplateModel";
import {DraggableTable} from "../App/DraggableTable/DraggableTable";
import {showMessage} from "../../utils/snackbar";

interface TemplateNewProps {
    onBack: () => void;
    onTemplateAdded: (newTemplate: TemplateModel) => void;
    onTemplateUpdated?: (updatedTemplate: TemplateModel) => void;
    isEditing?: boolean;
    initialTemplate?: TemplateModel;
}

const TemplateNew: React.FC<TemplateNewProps> = ({
                                                     onBack,
                                                     onTemplateAdded,
                                                     onTemplateUpdated,
                                                     isEditing = false,
                                                     initialTemplate
                                                 }) => {

    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [isManualAdd, setIsManualAdd] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [templateName, setTemplateName] = useState(
        initialTemplate?.name || "Template"
    );
    const [templateDescription, setTemplateDescription] = useState(
        initialTemplate?.description ||
        "Een chronologisch verslag van het gesprek met cliënt, patiënt en/of andere personen in een verhalende vorm."
    );
    const [templateItems, setTemplateItems] = useState<TemplateItemModel[]>(
        initialTemplate?.items || [
            {
                name: "Item 1",
                type: "tekst",
                description: "",
                examples: ""
            },
        ]
    );

    useEffect(() => {
        if (isManualAdd && bottomRef.current) {
            bottomRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
            setIsManualAdd(false);
        }
    }, [templateItems.length, isManualAdd]);

    const onSaveTemplate = async () => {
        if (!templateName.trim()) {
            showMessage("Geef een naam voor het sjabloon.", "error");
            return;
        }
        if (templateItems.length === 0) {
            showMessage("Voeg minstens één item toe.", "error");
            return;
        }

        const payload = {
            ...(isEditing && initialTemplate && {id: initialTemplate.id}),
            name: templateName,
            description: templateDescription,
            items: templateItems,
        };

        try {
            let template;
            if (isEditing && initialTemplate && initialTemplate.id) {
                // note: AI generated initial templates don't have an ID yet, they don't exist in the database.
                template = await patch(`/templates/${initialTemplate.id}`, payload);
                onTemplateUpdated?.(template);
            } else {
                template = await post("/templates", payload);
                onTemplateAdded(template);
            }
            onBack();
        } catch (error) {
            showMessage("Fout bij het opslaan van het sjabloon.", "error");
        }
    };

    const onAddItem = (index: number, newItem: TemplateItemModel) => {
        setTemplateItems((prev) => {
            const updatedItems = [...prev];
            updatedItems.splice(index, 0, newItem);
            return updatedItems;
        });
        setIsManualAdd(true);
    };

    const onUpdateItem = (
        index: number,
        field: keyof TemplateItemModel,
        value: string
    ) => {
        setTemplateItems((prev) => {
            const updated = [...prev];
            updated[index] = {...updated[index], [field]: value};
            return updated;
        });
    };

    const onCopyItem = (index: number) => {
        setTemplateItems((prev) => {
            const item = prev[index];
            const newItem = {...item, name: item.name + " (kopie)"};
            const updatedItems = [...prev];
            updatedItems.splice(index + 1, 0, newItem);
            return updatedItems;
        });
    };

    const onDeleteItem = (index: number) => {
        setTemplateItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleReorder = (reorderedItems: TemplateItemModel[]) => {
        setTemplateItems(reorderedItems);
    };

    const handleFileUpload = async (file: File) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            const uploadedTemplate = await post("/templates/upload", formData);
            setTemplateName(uploadedTemplate.name || "Template");
            setTemplateDescription(uploadedTemplate.description || "");
            setTemplateItems(uploadedTemplate.items || []);
            showMessage("Template succesvol geladen uit bestand.", "success");
        } catch (err) {
            showMessage("Fout bij het uploaden van het document.", "error");
        }
    };

    return (
        <Container maxWidth="md">
            <Button startIcon={<ArrowBackIcon/>} onClick={onBack} sx={{mb: 2}}>
                Terug naar lijst
            </Button>
            <Paper elevation={3} sx={{marginTop: 3}}>
                <Typography variant="h6" gutterBottom>
                    Nieuw template
                </Typography>
                <TextField
                    fullWidth
                    label="Naam"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    sx={{marginBottom: 2}}
                />
                <TextField
                    fullWidth
                    label="Beschrijving"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    multiline
                    rows={3}
                    sx={{marginBottom: 2}}
                />
                <Typography variant="h6" sx={{mb: 1}}>
                    Items:
                </Typography>

                <DraggableTable items={templateItems} onReorder={handleReorder}>
                    {(item: TemplateItemModel, index: number) => (
                        <Grid2 container spacing={2} width="100%">
                            <Grid2 size={11}>
                                <Grid2 container spacing={2} p={0}>
                                    <Grid2 size={6}>
                                        <TextField
                                            fullWidth
                                            label="Naam"
                                            value={item.name}
                                            onChange={(e) =>
                                                onUpdateItem(index, "name", e.target.value)
                                            }
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <FormControl fullWidth>
                                            <InputLabel
                                                id="template-label"
                                                sx={{backgroundColor: "white", px: 1}}
                                            >
                                                Type
                                            </InputLabel>
                                            <Select
                                                value={item.type}
                                                onChange={(e) =>
                                                    onUpdateItem(index, "type", e.target.value)
                                                }
                                                variant="filled"
                                            >
                                                <MenuItem value="tekst">Tekst</MenuItem>
                                                <MenuItem value="nummer">Nummer</MenuItem>
                                                <MenuItem value="ja/nee">Ja/Nee</MenuItem>
                                                <MenuItem value="datum">Datum</MenuItem>
                                                <MenuItem value="meerdere keuzes">
                                                    Meerdere Keuzes
                                                </MenuItem>
                                                <MenuItem value="tijd">Tijd</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>
                                </Grid2>
                                <Stack direction="column" mt='24px'>
                                    <TextField
                                        fullWidth
                                        label="Beschrijving"
                                        value={item.description}
                                        onChange={(e) =>
                                            onUpdateItem(index, "description", e.target.value)
                                        }
                                        multiline
                                        minRows={1}
                                        sx={{mb: 1}}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Voorbeelden"
                                        value={item.examples}
                                        onChange={(e) =>
                                            onUpdateItem(index, "examples", e.target.value)
                                        }
                                        multiline
                                        minRows={1}
                                        sx={{mb: '24px'}}
                                    />
                                </Stack>
                            </Grid2>
                            <Grid2 size={1}>
                                <Stack
                                    direction="column"
                                    justifyContent="space-between"
                                    height="100%"
                                >
                                    <IconButton
                                        onClick={() => onDeleteItem(index)}
                                        sx={{
                                            borderRadius: "50%",
                                        }}
                                    >
                                        <CloseIcon/>
                                    </IconButton>
                                    <IconButton
                                        onClick={() => onCopyItem(index)}
                                        sx={{
                                            borderRadius: "50%",
                                        }}
                                    >
                                        <ContentCopyIcon/>
                                    </IconButton>
                                </Stack>
                            </Grid2>
                        </Grid2>
                    )}
                </DraggableTable>

                <Button
                    variant="contained"
                    startIcon={<AddIcon/>}
                    onClick={() => onAddItem(templateItems.length, {
                        name: `Item ${templateItems.length + 1}`,
                        type: "tekst",
                        description: "",
                        examples: ""
                    })}
                    sx={{mt: 2}}
                >
                    Nieuw item toevoegen
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSaveTemplate}
                    sx={{mt: 2, ml: 2}}
                >
                    Opslaan
                </Button>
                <div ref={bottomRef}/>
            </Paper>
        </Container>
    );
};

export default TemplateNew;
